/**
 * Boot narration: a short checklist of what a cold load is actually doing,
 * shown over the blurred page so a 5–10 s wait says what it is waiting on.
 *
 * Every line reports from the signal that really gates it — Auth0's
 * `isLoading`, the scopes request, the expenses query in TanStack's cache,
 * Overview's per-bank balance fetches — so a line only turns to done when that
 * work has. Nothing here is timed or estimated.
 *
 * Steps register from wherever that knowledge lives: the shell-wide ones in
 * `useShellBootSteps()` (called by BootStatus), view-specific ones through
 * `useBootStep()` in the view. The narrator lists whichever were active this
 * boot, in a fixed order, so parallel work doesn't reshuffle the list.
 *
 * Boot-only by design. Once every step has been quiet for a moment the
 * narration ends for the session: in-app navigation is served from memory, and
 * later refreshes have their own indicators (the account pill's spinner, the
 * mobile top bar's pip).
 */
import {
  computed,
  effectScope,
  onScopeDispose,
  ref,
  shallowReactive,
  shallowRef,
  watch,
  watchEffect,
  type ComputedRef,
  type Ref,
} from 'vue'
import { START_LOCATION, useRouter } from 'vue-router'
import { useQueryClient } from '@tanstack/vue-query'
import { useEncryption } from '@/composables/useEncryption'
import { useGlobalScopeFilter } from '@/composables/useGlobalScopeFilter'
import { useExpenseHistory } from '@/composables/useExpenseHistory'
import { categoryColor } from '@/utils/categoryColor'

export type BootStepId = 'session' | 'scopes' | 'transactions' | 'balances'

/** Display order. Transactions and balances load in parallel; a fixed order
 *  keeps the list from reshuffling as they finish. */
const ORDER: readonly BootStepId[] = ['session', 'scopes', 'transactions', 'balances']

export interface BootStepReport {
  /** Cold work the page is waiting on is in flight. */
  active: boolean
  /** Finished without a result. */
  failed?: boolean
  /** Shown while active — what is happening now. */
  label: string
  /** Shown once finished — what it produced. */
  doneLabel: string
  /** Shown if it failed. */
  failLabel?: string
}

export interface BootLine {
  id: BootStepId
  state: 'active' | 'done' | 'failed'
  text: string
}

/** A boot that's over this quickly just lifts the veil: the "all set" moment
 *  is for waits long enough to have been watched. */
const QUICK_MS = 800
/** Quiet time before boot counts as over. Covers the gaps between steps (auth
 *  resolving → the view mounting → its queries starting), which would
 *  otherwise end the narration between one step and the next. */
const IDLE_MS = 400
/** How long the all-done state stays up before it fades — long enough for
 *  the check mark to finish drawing. */
const HOLD_MS = 900

const reporters = shallowReactive(new Map<BootStepId, ComputedRef<BootStepReport>>())
// Steps that were active at some point this boot. Only these are listed, so a
// step whose data was already in hand never shows up as a line.
const seen = shallowReactive(new Set<BootStepId>())
const settling = ref(false)
const finished = ref(false)
// The viewer waved the overlay away (click or Esc). Boot carries on; the page
// just shows its skeletons as it did before the overlay existed.
const dismissed = ref(false)
let started = false

// Up from the very first frame the shell draws. It used to wait half a second
// and fade in, which showed the un-blurred page first and then blurred it — the
// wrong way round for something meant to sit in front of the loading page.
const visible = computed(() => !finished.value && !dismissed.value)

export interface StreamRow {
  name: string
  amount: number
  color: string
}
// A handful of the user's most recent transactions, taken from the expenses
// response the moment it lands, so the overlay's stream switches from
// placeholder rows to real ones. Rows whose names are sealed are skipped.
const streamSample = shallowRef<StreamRow[]>([])

/** True until boot narration is over. The mobile top bar holds back its
 *  generic "Refreshing…" pip for this long: during boot it would only repeat
 *  the overlay. */
export const bootInProgress = computed(() => !finished.value)

/** Report a step from wherever its signal lives. Unregisters with the calling
 *  component (or effect scope). A no-op once boot is over. */
export function useBootStep(id: BootStepId, report: () => BootStepReport): void {
  if (finished.value) return
  const c = computed(report)
  reporters.set(id, c)
  onScopeDispose(() => {
    if (reporters.get(id) === c) reporters.delete(id)
  })
}

// Runs in a detached scope rather than the caller's component: the shell can
// unmount and remount mid-boot (a bare route in between), and the boot clock
// shouldn't restart with it.
function startNarration(): void {
  if (started || finished.value) return
  started = true
  const scope = effectScope(true)
  scope.run(() => {
    const anyActive = computed(() => {
      for (const r of reporters.values()) if (r.value.active) return true
      return false
    })

    watchEffect(() => {
      for (const [id, r] of reporters) if (r.value.active) seen.add(id)
    })

    const startedAt = performance.now()

    let idleTimer: number | undefined
    let holdTimer: number | undefined
    const finish = () => {
      finished.value = true
      reporters.clear()
      scope.stop()
    }
    watch(
      anyActive,
      (active) => {
        window.clearTimeout(idleTimer)
        if (active) {
          // Something started again during the hold — keep narrating.
          window.clearTimeout(holdTimer)
          settling.value = false
          return
        }
        idleTimer = window.setTimeout(() => {
          if (performance.now() - startedAt < QUICK_MS) return finish()
          settling.value = true
          holdTimer = window.setTimeout(finish, HOLD_MS)
        }, IDLE_MS)
      },
      { immediate: true },
    )
  })
}

/** What BootStatus renders. Starts the boot clock on first use. */
export function useBootNarrator() {
  startNarration()

  const lines = computed<BootLine[]>(() =>
    ORDER.flatMap((id): BootLine[] => {
      const r = reporters.get(id)?.value
      if (!r || !seen.has(id)) return []
      if (r.active) return [{ id, state: 'active', text: r.label }]
      if (r.failed) return [{ id, state: 'failed', text: r.failLabel ?? r.label }]
      return [{ id, state: 'done', text: r.doneLabel }]
    }),
  )

  // The one line a screen reader hears: what is happening now, then the
  // outcome. Silent for loads too quick to show the card.
  const announcement = computed(() => {
    if (!visible.value) return ''
    const active = lines.value.find((l) => l.state === 'active')
    if (active) return active.text
    return settling.value ? 'Everything has loaded' : ''
  })

  return {
    lines,
    visible,
    settling,
    announcement,
    streamRows: streamSample,
    dismiss: () => { dismissed.value = true },
  }
}

const count = (n: number) => n.toLocaleString()

/** The steps every page shares: signing in, scopes, transactions. */
export function useShellBootSteps(auth: { isLoading?: Ref<boolean> }): void {
  if (finished.value) return
  const router = useRouter()
  const queryClient = useQueryClient()
  const enc = useEncryption()
  const { depth } = useExpenseHistory()
  const { globalAvailableScopes, isLoadingScopes } = useGlobalScopeFilter()

  // Auth0 restoring the session, then the guarded first navigation committing
  // (which is also when the page's code downloads). After a fresh login the
  // shell mounts once this is over, so the line simply never appears.
  useBootStep('session', () => ({
    active: !!auth.isLoading?.value || router.currentRoute.value === START_LOCATION,
    label: 'Signing you in',
    doneLabel: 'Signed in',
  }))

  useBootStep('scopes', () => {
    const n = globalAvailableScopes.value.length
    return {
      active: isLoadingScopes.value,
      failed: n === 0,
      label: 'Loading your scopes',
      doneLabel: n === 1 ? '1 scope' : `${n} scopes`,
      failLabel: "Couldn't load your scopes",
    }
  })

  // TanStack's cache isn't reactive to Vue, so a tick re-runs the step on every
  // cache event. Dropped once boot is over.
  const cacheTick = ref(0)
  const unsubscribe = queryClient.getQueryCache().subscribe(() => { cacheTick.value++ })
  const stopWatch = watch(finished, (done) => { if (done) unsubscribe() })
  onScopeDispose(() => { unsubscribe(); stopWatch() })

  // Sample once, when the first expenses response arrives.
  const stopSample = watch(cacheTick, () => {
    if (streamSample.value.length) return stopSample()
    const rows = queryClient.getQueryState<{ expenses?: any[] }>(['expenses', depth.value])?.data?.expenses
    if (!rows?.length) return
    streamSample.value = rows
      .map((r) => ({ r, name: r?.MerchantName || r?.PlaidMerchantName || r?.PlaidName, t: Date.parse(r?.ExpenseDate) || 0 }))
      .filter((x) => typeof x.name === 'string' && x.name.trim())
      .sort((a, b) => b.t - a.t)
      .slice(0, 12)
      .map(({ r, name }) => ({
        name: String(name),
        amount: Math.abs(Number(r?.Amount) || 0),
        color: categoryColor(r?.ExpenseCategory),
      }))
    stopSample()
  })
  onScopeDispose(stopSample)

  useBootStep('transactions', () => {
    void cacheTick.value
    // The cache holds the raw response; useExpensesQuery's `select` unwraps it.
    const state = queryClient.getQueryState<{ expenses?: unknown[] }>(['expenses', depth.value])
    const rows = state?.data?.expenses
    if (!rows) {
      return {
        active: state?.fetchStatus === 'fetching',
        failed: state?.status === 'error',
        label: 'Fetching your transactions',
        doneLabel: 'No transactions yet',
        failLabel: "Couldn't load transactions",
      }
    }
    // Sealed rows stay unreadable until this tab holds a private key; from
    // then on useExpensesQuery opens them synchronously. Waiting for that key
    // is the step.
    const sealed = (enc.state as any).privateKey
      ? 0
      : rows.filter((r: any) => typeof r?.EncryptedPayload === 'string' && r.EncryptedPayload).length
    return {
      active: sealed > 0,
      label: enc.status.value === 'locked'
        ? 'Waiting for you to unlock'
        : `Decrypting ${count(sealed)} transactions`,
      doneLabel: rows.length ? `${count(rows.length)} transactions` : 'No transactions yet',
    }
  })
}
